// ============================================================================
// SCALE STATE — Handoff (100%) · Comfortable (115%) · Large (130%).
//
// Owner request, 2026-08-26: "the whole UI feels too tiny," and he has been
// pressing Ctrl+= on every fresh load to compensate. This makes that permanent
// and app-wide instead of a per-tab habit that resets on every session — which
// is also why DEFAULT_SCALE is 'large': the owner's own workaround IS the
// requested default, not an option he has to find.
//
// Same shape as theme.ts on purpose: one namespaced localStorage key, a pure
// resolve-to-a-number step, and a DOM-stamping function that takes its world as
// an argument (testable without a browser — see scale.test.ts). useTheme.ts's
// module store carries both this and the theme choice, so the two read as one
// idiom rather than two.
// ============================================================================

export type ScaleChoice = 'handoff' | 'comfortable' | 'large'

export const SCALE_CHOICES: readonly ScaleChoice[] = ['handoff', 'comfortable', 'large']

/** The localStorage key. Namespaced like THEME_STORAGE_KEY. */
export const SCALE_STORAGE_KEY = 'lifeos.scale'

/** The default: the owner's own Ctrl+= habit, made permanent rather than repeated every session. */
export const DEFAULT_SCALE: ScaleChoice = 'large'

/** What each choice actually sets CSS `zoom` to. */
export const SCALE_FACTOR: Record<ScaleChoice, number> = {
  handoff: 1,
  comfortable: 1.15,
  large: 1.3,
}

function isScaleChoice(value: unknown): value is ScaleChoice {
  return value === 'handoff' || value === 'comfortable' || value === 'large'
}

/**
 * What the owner last chose, or the default. Same corrupt-value handling as
 * readChoice in theme.ts: a bad or stale read degrades to the default rather
 * than throwing on the render path, and `localStorage` itself throws in a
 * locked-down browser profile, so the read is guarded too.
 */
export function readScale(storage: Pick<Storage, 'getItem'> | null | undefined): ScaleChoice {
  try {
    const raw = storage?.getItem(SCALE_STORAGE_KEY)
    return isScaleChoice(raw) ? raw : DEFAULT_SCALE
  } catch {
    return DEFAULT_SCALE
  }
}

/** Persist the choice. */
export function writeScale(
  storage: Pick<Storage, 'setItem'> | null | undefined,
  choice: ScaleChoice,
): void {
  try {
    storage?.setItem(SCALE_STORAGE_KEY, choice)
  } catch {
    // A browser that refuses storage still gets a working control for this session. Silently
    // losing the preference beats refusing to switch.
  }
}

/**
 * Stamp the document. `zoom` goes on `<html>` — not on `#root` or any inner wrapper — because that
 * is the one place Chromium and Firefox 126+ treat it as real page zoom rather than a plain
 * magnified box: `100vh`/`100vw` and the page's own responsive breakpoints keep meaning "the whole
 * viewport" at every scale, instead of "100% of the UNSCALED viewport" the way a `transform:
 * scale()` (or `zoom` on an inner element sized by height:100%) would — which is exactly what would
 * overflow the screen at 130%. `data-scale-choice` mirrors `data-theme-choice`: it drives the
 * control's own highlighted state and gives anyone reading the DOM the raw choice, not just the
 * number.
 */
export function applyScale(doc: Document | null | undefined, choice: ScaleChoice): void {
  const root = doc?.documentElement
  if (!root) return
  root.style.zoom = String(SCALE_FACTOR[choice])
  // The same factor as a custom property, for the one thing that must NOT scale with it: the
  // phone's tab bar and + menu are drawn at the mobile handoff's own pixel sizes whatever the
  // size setting (owner, 2026-10-19), so they counter-zoom by `1 / --app-zoom` (index.css).
  root.style.setProperty('--app-zoom', String(SCALE_FACTOR[choice]))
  root.setAttribute('data-scale-choice', choice)
}
